import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../context/ApiContext";
import { IPageBuilder, IComponentNode } from "../../types/pageBuilder.types";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Alert } from "../../components/ui/Alert";
import { Spinner } from "../../components/ui/Spinner";
import { PageBuilder as PageBuilderComponent } from "../../components/pageBuilder/PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";

const COMMON_ZONE_NAMES = ["header", "footer", "sidebar", "content"];

export const PageZones: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [searchParams, setSearchParams] = useSearchParams();
 const [pages, setPages] = useState<IPageBuilder[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [editingZone, setEditingZone] = useState<{ page: IPageBuilder; zoneName: string } | null>(null);
 const [showZoneManager, setShowZoneManager] = useState(false);
 const [selectedPage, setSelectedPage] = useState<IPageBuilder | null>(null);
 const [newZoneName, setNewZoneName] = useState("");
 const [addingZones, setAddingZones] = useState(false);

 useEffect(() => {
  loadPages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 // Handle URL parameters for direct zone editing
 useEffect(() => {
  const pageId = searchParams.get("pageId");
  const pageType = searchParams.get("pageType");
  const zoneName = searchParams.get("zoneName");

  if (zoneName && (pageId || pageType)) {
   const findAndEditZone = async () => {
    try {
     let page: IPageBuilder | null = null;
     if (pageId) {
      const response = await api.get(`/page-builder/pages/${pageId}`);
      page = response.data.page;
     } else if (pageType) {
      const response = await api.get(`/page-builder/pages/type/${pageType}`);
      page = response.data.page;
     }

     if (page && page.zones && page.zones[zoneName]) {
      setEditingZone({ page, zoneName });
      // Clear URL params
      setSearchParams({});
     }
    } catch (err) {
     console.error("Error loading page for zone editing:", err);
    }
   };

   if (pages.length > 0) {
    findAndEditZone();
   }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchParams, pages.length]);

 const loadPages = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await api.get("/page-builder/pages?includeUnpublished=true");
   const allPages = response.data.pages || [];
   // Filter only pages that support zones
   const zonePages = allPages.filter((page: IPageBuilder) =>
    supportsZones(page.pageType)
   );
   setPages(zonePages);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pageZones.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const supportsZones = (pageType: string): boolean => {
  return ["home", "checkout", "product-detail", "product-list", "custom"].includes(pageType);
 };

 const getPageZones = (page: IPageBuilder): string[] => {
  return Object.keys(page.zones || {});
 };

 const handleManageZones = async (page: IPageBuilder) => {
  try {
   const response = await api.get(`/page-builder/pages/${page._id}`);
   const updatedPage = response.data.page;
   setSelectedPage(updatedPage);
   setShowZoneManager(true);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to load page");
  }
 };

 const handleEditZone = async (page: IPageBuilder, zoneName: string) => {
  try {
   const response = await api.get(`/page-builder/pages/${page._id}`);
   const updatedPage = response.data.page;
   setEditingZone({ page: updatedPage, zoneName });
   setShowZoneManager(false);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to load page");
  }
 };

 const handleAddZone = async (page: IPageBuilder, zoneName: string) => {
  if (!zoneName.trim()) {
   setError(t("admin.pageZones.zoneNameRequired"));
   return;
  }

  try {
   setError(null);
   const currentZones = page.zones || {};
   const newZone: IComponentNode = {
    id: `zone-${zoneName}-${Date.now()}`,
    type: "container",
    props: {},
    styles: { default: {} },
    children: [],
   };

   const updatedZones = {
    ...currentZones,
    [zoneName]: newZone,
   };

   await api.put(`/page-builder/pages/${page._id}`, {
    zones: updatedZones,
   });

   await loadPages();
   if (selectedPage?._id === page._id) {
    const response = await api.get(`/page-builder/pages/${page._id}`);
    setSelectedPage(response.data.page);
   }
   setNewZoneName("");
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pageZones.errors.addZones"));
  }
 };

 const handleDeleteZone = async (page: IPageBuilder, zoneName: string) => {
  if (!window.confirm(`Are you sure you want to delete the "${zoneName}" zone?`)) {
   return;
  }

  try {
   setError(null);
   const currentZones = { ...(page.zones || {}) };
   delete currentZones[zoneName];

   await api.put(`/page-builder/pages/${page._id}`, {
    zones: currentZones,
   });

   await loadPages();
   if (selectedPage?._id === page._id) {
    const response = await api.get(`/page-builder/pages/${page._id}`);
    setSelectedPage(response.data.page);
   }
   if (editingZone?.page._id === page._id && editingZone.zoneName === zoneName) {
    setEditingZone(null);
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pageZones.errors.deleteZone"));
  }
 };

 const handleDeletePage = async (page: IPageBuilder) => {
  if (!window.confirm(`Are you sure you want to delete the page "${page.name}"? This will also delete all its zones. This action cannot be undone.`)) {
   return;
  }

  try {
   setError(null);
   await api.delete(`/page-builder/pages/${page._id}`);

   // Close any open modals/editors if they're for this page
   if (selectedPage?._id === page._id) {
    setShowZoneManager(false);
    setSelectedPage(null);
   }
   if (editingZone?.page._id === page._id) {
    setEditingZone(null);
   }

   await loadPages();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pageZones.errors.deletePage"));
  }
 };

 const handleSaveZone = async (zoneLayout: IComponentNode) => {
  if (!editingZone) return;

  try {
   setError(null);
   const currentZones = { ...(editingZone.page.zones || {}) };
   currentZones[editingZone.zoneName] = zoneLayout;

   await api.put(`/page-builder/pages/${editingZone.page._id}`, {
    zones: currentZones,
   });

   await loadPages();
   setEditingZone(null);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pageZones.errors.saveZone"));
  }
 };

 const handleAddMissingZones = async () => {
  try {
   setAddingZones(true);
   setError(null);
   const response = await api.post("/page-builder/pages/add-missing-zones");
   await loadPages();
   // Show success message with details
   const message = response.data.message || t("admin.pageZones.zonesAdded");
   alert(message);
  } catch (err: any) {
   const errorMessage = err.response?.data?.error || err.message || t("admin.pageZones.errors.addZones");
   setError(errorMessage);
   console.error("Error adding missing zones:", err);
   alert(`Error: ${errorMessage}`);
  } finally {
   setAddingZones(false);
  }
 };

 // Zone editor view
 if (editingZone) {
  const zoneContent = editingZone.page.zones?.[editingZone.zoneName] || null;
  return (
   <PageBuilderProvider initialLayout={zoneContent}>
    <div className="fixed inset-0 flex flex-col bg-gray-100 z-50">
     <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div>
       <Heading size="lg">
        Edit Zone: {editingZone.zoneName} ({editingZone.page.name})
       </Heading>
       <p className="text-sm text-gray-500 mt-1">
        This zone will appear on all pages with type: <strong>{editingZone.page.pageType}</strong>
       </p>
      </div>
      <Button
       variant="outline"
       onClick={() => {
        setEditingZone(null);
        setShowZoneManager(true);
        setSelectedPage(editingZone.page);
       }}
      >
       Back to Zones
      </Button>
     </div>
     <div className="flex-1 overflow-hidden">
      <PageBuilderComponent
       initialLayout={zoneContent}
       onCancel={() => {
        setEditingZone(null);
        setShowZoneManager(true);
        setSelectedPage(editingZone.page);
       }}
       onSave={handleSaveZone}
       pageName={editingZone.page.name}
       zoneName={editingZone.zoneName}
       pageType={editingZone.page.pageType}
      />
     </div>
    </div>
   </PageBuilderProvider>
  );
 }

 // Zone manager modal
 if (showZoneManager && selectedPage) {
  const pageZones = getPageZones(selectedPage);

  return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <Card padding="lg" className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
     <Stack spacing="md">
      <div className="flex items-center justify-between">
       <Heading size="xl">Manage Zones: {selectedPage.name}</Heading>
       <Button
        variant="outline"
        onClick={() => {
         setShowZoneManager(false);
         setSelectedPage(null);
         setError(null);
         setNewZoneName("");
        }}
       >
        Close
       </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div>
       <Heading size="md" className="mb-3">Existing Zones</Heading>
       {pageZones.length === 0 ? (
        <p className="text-gray-500 text-sm">No zones configured yet.</p>
       ) : (
        <div className="space-y-2">
         {pageZones.map((zoneName) => (
          <div
           key={zoneName}
           className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200"
          >
           <div>
            <span className="font-medium text-gray-900">{zoneName}</span>
            <span className="text-sm text-gray-500 ml-2">
             ({selectedPage.zones?.[zoneName]?.children?.length || 0} components)
            </span>
           </div>
           <div className="flex gap-2">
            <Button
             variant="secondary"
             size="sm"
             onClick={() => handleEditZone(selectedPage, zoneName)}
            >
             Edit
            </Button>
            <Button
             variant="danger"
             size="sm"
             onClick={() => handleDeleteZone(selectedPage, zoneName)}
            >
             Delete
            </Button>
           </div>
          </div>
         ))}
        </div>
       )}
      </div>

      <div>
       <Heading size="md" className="mb-3">Add New Zone</Heading>
       <div className="flex gap-2">
        <input
         type="text"
         placeholder={t("common.placeholders.zoneName")}
         value={newZoneName}
         onChange={(e) => setNewZoneName(e.target.value)}
         onKeyPress={(e) => {
          if (e.key === "Enter") {
           handleAddZone(selectedPage, newZoneName);
          }
         }}
         className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Button
         variant="primary"
         onClick={async () => {
          await handleAddZone(selectedPage, newZoneName);
         }}
         disabled={!newZoneName.trim()}
        >
         Add Zone
        </Button>
       </div>
       <p className="text-xs text-gray-500 mt-2">
        Common zone names: {COMMON_ZONE_NAMES.join(", ")}
       </p>
      </div>
     </Stack>
    </Card>
   </div>
  );
 }

 if (loading) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <div>
      <Heading size="xl">{t("admin.pageZones.title")}</Heading>
      <p className="text-sm text-gray-500 mt-1">
       {t("admin.pageZones.description")}
      </p>
     </div>
      <Button
       variant="primary"
       onClick={handleAddMissingZones}
       disabled={addingZones}
      >
       {addingZones ? t("admin.pageZones.addingZones") : t("admin.pageZones.addAllMissingZones")}
      </Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    {pages.length === 0 ? (
     <Card padding="lg">
      <div className="text-center py-8">
       <p className="text-gray-500 mb-4">{t("admin.pageZones.noPages")}</p>
       <p className="text-sm text-gray-400">
        {t("admin.pageZones.descriptionNoZones")}
       </p>
      </div>
     </Card>
    ) : (
     <Card padding="lg">
      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
           {t("admin.pageBuilder.pageName")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
           {t("admin.pageZones.pageType")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
           {t("admin.pageZones.zones")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
           {t("admin.status")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
           {t("admin.actions")}
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {pages.map((page) => (
          <tr key={page._id}>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {page.name}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            <div className="flex items-center gap-2">
             <span>{page.pageType}</span>
             <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 ">
              {t("admin.pageZones.zones")}
             </span>
            </div>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            <span className={getPageZones(page).length > 0 ? "text-green-600 font-medium" : ""}>
             {getPageZones(page).length > 0
              ? t("admin.pageZones.zonesCount", { count: getPageZones(page).length })
              : t("admin.pageZones.noZones")}
            </span>
           </td>
           <td className="px-6 py-4 whitespace-nowrap">
            <span
             className={`px-2 inline-flex text-xs leading-5 font-semibold ${
              page.isPublished
               ? "bg-green-100 text-green-800"
               : "bg-gray-100 text-gray-800"
             }`}
            >
             {page.isPublished ? t("admin.pageZones.published") : t("admin.pageZones.draft")}
            </span>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div className="flex gap-2">
             <Button
              variant="secondary"
              size="sm"
              onClick={() => handleManageZones(page)}
             >
              {t("admin.pageZones.manageZones")}
             </Button>
             <Button
              variant="danger"
              size="sm"
              onClick={() => handleDeletePage(page)}
             >
              {t("admin.pageZones.deletePage")}
             </Button>
            </div>
           </td>
          </tr>
         ))}
        </tbody>
       </table>
      </div>
     </Card>
    )}
   </Stack>
  </Container>
 );
};

export default PageZones;

